import { format, parse } from 'date-fns';
import * as React from 'react';
import { BusinessHours, SpecialHours } from '../stores/StoresStore';

interface BaseProps {
  language: string;
  translate: (key: string) => string;
}

interface OpeningHoursProps extends BaseProps {
  hours: BusinessHours | SpecialHours;
  addOpenTranslation?: boolean;
}

export interface SpecialHoursProps extends BaseProps {
  hours: SpecialHours[];
}

export interface BusinessHoursProps extends BaseProps {
  hours: BusinessHours[];
}

interface BusinessHourProps extends BaseProps {
  hour: BusinessHours;
}

interface SpecialHourProps extends BaseProps {
  hour: SpecialHours;
}

export const BusinessOpeningHours = ({ hours, translate, language }: BusinessHoursProps): JSX.Element => {
  if (!hours || (hours.length === 1 && hours[0].closed)) {
    return null;
  }
  return <div>{hours.map((hour) => getBusinessHoursForPeriod({ hour, translate, language })).join(', ')}</div>;
};

export const SpecialOpeningHours = ({ hours, translate, language }: SpecialHoursProps): JSX.Element => {
  if (!hours) {
    return null;
  }
  return (
    <>
      {hours.map((hour, index) => (
        <div key={index}>{getSpecialHoursForPeriod({ hour, translate, language })}</div>
      ))}
    </>
  );
};

const getBusinessHoursForPeriod = ({ hour, translate, language }: BusinessHourProps): string => {
  let openingHours = translate(`store.hours.${hour.startDay}`);

  if (hour.startDay !== hour.endDay) {
    openingHours = openingHours.concat('-').concat(translate(`store.hours.${hour.endDay}`));
  }
  return openingHours.concat(' ').concat(getOpeningHours({ hours: hour, language, translate }));
};

const getSpecialHoursForPeriod = ({ hour, translate, language }: SpecialHourProps): string => {
  const formatDate = (date: string) => format(parse(date, 'yyyy-MM-dd', new Date()), 'dd.MM');
  let openingHours = formatDate(hour.startDate);

  if (hour.startDate !== hour.endDate) {
    openingHours = openingHours.concat('-').concat(formatDate(hour.endDate));
  }
  return openingHours.concat(' ').concat(getOpeningHours({ hours: hour, language, translate, addOpenTranslation: true }));
};

const getOpeningHours = ({ hours, translate, language, addOpenTranslation }: OpeningHoursProps): string => {
  if (hours.closed) {
    return translate('store.hours.closed');
  }
  const formatTime = (time: string) => (language === 'et' ? time.replace(':', '.') : time);
  let openingHours = '';

  if (addOpenTranslation) {
    openingHours = openingHours.concat(translate('store.hours.open')).concat(' ');
  }
  return openingHours.concat(formatTime(hours.openTime)).concat('-').concat(formatTime(hours.closeTime));
};
